import { createSlice, WithSlice } from '@reduxjs/toolkit';
import {
  QUOTE_BUILDER_PART_INSIGHTS_SLICE_NAME,
  QUOTE_BUILDER_SLICE_NAME,
} from '@ui/constants/quote-builder';
import { createSnapQuoteAssignmentReducer } from '@ui/features/quote-builder/state/extra-reducers/create-snap-quote-assignment';
import { createSnapQuoteAttachmentReducer } from '@ui/features/quote-builder/state/extra-reducers/create-snap-quote-attachment';
import { deleteSnapQuoteAttachmentReducer } from '@ui/features/quote-builder/state/extra-reducers/delete-snap-quote-attachments';
import { updateAndAssignQuoteReducer } from '@ui/features/quote-builder/state/extra-reducers/update-and-assign-quote';
import { rootReducer } from '@ui/store';

import { reducers } from './actions';
import { deleteSnapQuoteLine } from './endpoints/delete-snap-quote-line';
import { editSnapQuote } from './endpoints/edit-snap-quote';
import { createNewPartUserPriceQuoteLineReducer } from './extra-reducers/create-new-part-user-price-quote-line';
import { createQuoteLineOptimisticallyReducer } from './extra-reducers/create-quote-line-optimistically';
import { createSnapQuoteLineReducer } from './extra-reducers/create-snap-quote-line';
import { createSnapQuoteLineAttachmentReducer } from './extra-reducers/create-snap-quote-line-attachment';
import { createSnapQuotePriceReducer } from './extra-reducers/create-snap-quote-price';
import { deleteSnapQuoteLineAttachmentReducer } from './extra-reducers/delete-snap-quote-line-attachments';
import { deleteSnapQuotePriceReducer } from './extra-reducers/delete-snap-quote-price';
import { editSnapQuoteReducer } from './extra-reducers/edit-snap-quote';
import { getOrdersAndQuotesMatchingIdInsightReducer } from './extra-reducers/get-orders-and-quotes-matching-id-insight';
import { getSnapQuotePartInsightsReducer } from './extra-reducers/get-snap-quote-part-insights';
import { importSpreadsheetReducer } from './extra-reducers/import-spreadsheet';
import { issuesDrawerReducer } from './extra-reducers/issues-drawer';
import { createSendQuoteReducer } from './extra-reducers/send-quote';
import { showSalesRepReducer } from './extra-reducers/show-sales-rep';
import { updateSnapQuoteReducer } from './extra-reducers/update-snap-quote';
import {
  defaultPartInsightsState,
  defaultState,
} from './utilities/default-state';

const quoteBuilderReducer = createSlice({
  name: QUOTE_BUILDER_SLICE_NAME,
  initialState: defaultState,
  reducers,
  // @TODO(#2403): Replace with imported selectors once Root state type guards are removed
  selectors: {
    selectQuoteBuilder: (state) => state,
  },
  extraReducers: (builder) => {
    createNewPartUserPriceQuoteLineReducer(builder);
    createQuoteLineOptimisticallyReducer(builder);
    createSendQuoteReducer(builder);
    createSnapQuoteAssignmentReducer(builder);
    createSnapQuoteLineAttachmentReducer(builder);
    createSnapQuoteLineReducer(builder);
    createSnapQuotePriceReducer(builder);
    deleteSnapQuotePriceReducer(builder);
    deleteSnapQuoteLineAttachmentReducer(builder);
    editSnapQuoteReducer(builder);
    updateSnapQuoteReducer(builder);
    importSpreadsheetReducer(builder);
    issuesDrawerReducer(builder);
    showSalesRepReducer(builder);
    updateAndAssignQuoteReducer(builder);
    createSnapQuoteAttachmentReducer(builder);
    deleteSnapQuoteAttachmentReducer(builder);
  },
});

const quoteBuilderPartInsightsReducer = createSlice({
  name: QUOTE_BUILDER_PART_INSIGHTS_SLICE_NAME,
  initialState: defaultPartInsightsState,
  reducers: {},
  selectors: {
    selectQuoteBuilderPartInsights: (state) => state,
  },
  extraReducers: (builder) => {
    getOrdersAndQuotesMatchingIdInsightReducer(builder);
    getSnapQuotePartInsightsReducer(builder);
    builder.addMatcher(editSnapQuote.matchFulfilled, (state) => {
      Object.assign(state, defaultPartInsightsState);
    });
    builder.addMatcher(deleteSnapQuoteLine.matchFulfilled, (state, action) => {
      const { quoteLineUuid } = action.meta.arg.originalArgs;
      delete state[quoteLineUuid];
    });
  },
});

export const quoteBuilderSlice = quoteBuilderReducer.injectInto(rootReducer);

quoteBuilderPartInsightsReducer.injectInto(rootReducer);

export const { actions } = quoteBuilderSlice;

declare module '@ui/store' {
  export interface LazyLoadedSlices
    extends WithSlice<typeof quoteBuilderReducer>,
      WithSlice<typeof quoteBuilderPartInsightsReducer> {}
}
